import { z } from "zod";
import { NextActivitySchema, ObjectiveProgressSchema } from "./objectives.js";
/**
 * The shapes behind the teacher's three screens (ADR-011).
 *
 * These replace the parent report wholesale. The old surface could only say sentences a
 * claims lexicon had licensed, because it was talking to a family about their child's mind.
 * This one talks to a teacher about work done in their own room, so it says counts and levels
 * and names — and the honesty problem moves from "which sentences are sayable" to "how few
 * trials may a comparison rest on", which is what the floors below are for.
 */
/**
 * The fewest scored trials a student must have in a game before their number is allowed into
 * a class comparison.
 *
 * Without a floor, the class table is dominated by whoever opened a game once: the adaptive
 * ladder starts everyone at the bottom, so a single visit reads as a low level and a child who
 * has barely played looks like a child who is struggling. Five is the point where the ladder
 * has had a chance to move at all.
 */
export const MIN_TRIALS_FOR_CLASS_COMPARISON = 5;
/**
 * How far below the class median counts as "worth a look".
 *
 * A whole level, because levels are the unit the teacher acts on and anything finer is noise
 * dressed as a finding. Being fractionally below a median is what half of any class is, by
 * definition, and flagging it would mean flagging half the room.
 */
export const MIN_LEVEL_GAP_FOR_HELP = 1;
/**
 * The fewest students a class median may rest on.
 *
 * Two is not a middle, it is a pair — and "below the median" from two children means only "the other
 * one is ahead", which is a weak inference stated strongly on a screen headed *who needs help*. Three
 * is the smallest number for which a middle exists at all.
 *
 * This exists because the comment on `median` below already promised the screen would refuse a median
 * resting on two children, and nothing did: the count travelled and neither the server nor the screen
 * read it. A promise in a comment that no code keeps is worse than no promise.
 */
export const MIN_STUDENTS_FOR_MEDIAN = 3;
/** How far a student has got in one game. */
export const GameReachSchema = z.object({
    family: z.string().min(1),
    /** The task actually played, so a link can open it. */
    taskId: z.string().min(1),
    reached: z.number().int().nonnegative(),
    max: z.number().int().positive(),
    trials: z.number().int().nonnegative(),
    correct: z.number().int().nonnegative(),
    lastPlayedAt: z.string().datetime().nullable(),
});
/** One row of the roster. */
export const StudentSummarySchema = z.object({
    studentId: z.string().uuid(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    phone: z.string().min(1),
    sessions: z.number().int().nonnegative(),
    /** Distinct days played. Two sittings in one afternoon are one day of practice. */
    daysPlayed: z.number().int().nonnegative(),
    lastPlayedAt: z.string().datetime().nullable(),
    openAssignments: z.number().int().nonnegative(),
    masteredObjectives: z.number().int().nonnegative(),
    /** What to give them next, and why. */
    next: NextActivitySchema,
});
export const RosterSchema = z.object({
    teacherFirstName: z.string().nullable(),
    students: z.array(StudentSummarySchema),
});
/** One student, in full. */
export const StudentDetailSchema = z.object({
    student: StudentSummarySchema,
    objectives: z.array(ObjectiveProgressSchema),
    games: z.array(GameReachSchema),
});
/**
 * The comparison table: students down, games across.
 *
 * `cells` is a flat list rather than a nested matrix because most of the matrix is empty —
 * a class that has played four of fifteen games would otherwise ship eleven columns of nulls
 * on every request.
 */
export const ClassCellSchema = z.object({
    studentId: z.string().uuid(),
    family: z.string().min(1),
    reached: z.number().int().nonnegative(),
    trials: z.number().int().nonnegative(),
    /** Proportion correct, or null below the trial floor. */
    accuracy: z.number().min(0).max(1).nullable(),
});
export const ClassComparisonSchema = z.object({
    games: z.array(z.object({
        family: z.string().min(1),
        taskId: z.string().min(1),
        max: z.number().int().positive(),
        /**
         * Median level reached across students who cleared the trial floor, and how many that
         * was. Both the screen and the needs-a-look list refuse a median resting on fewer than
         * `MIN_STUDENTS_FOR_MEDIAN` children.
         */
        median: z.number().nullable(),
        from: z.number().int().nonnegative(),
    })),
    students: z.array(z.object({
        studentId: z.string().uuid(),
        firstName: z.string().nullable(),
        lastName: z.string().nullable(),
    })),
    cells: z.array(ClassCellSchema),
    /**
     * Students sitting a level or more below the class median in a game.
     *
     * Two floors, and both are needed. Each child must have cleared `MIN_TRIALS_FOR_CLASS_COMPARISON`,
     * because one visit to a game is indistinguishable from struggling at it; and the median itself must
     * rest on `MIN_STUDENTS_FOR_MEDIAN` children, because from two it says only "the other one is
     * ahead". Computed on the server so the two surfaces cannot disagree about who is on this list.
     */
    needsHelp: z.array(z.object({
        studentId: z.string().uuid(),
        family: z.string().min(1),
        reached: z.number().int().nonnegative(),
        median: z.number(),
    })),
});
/** The objective × student grid, for the screen a teacher assigns work from. */
export const ClassObjectivesSchema = z.object({
    students: z.array(z.object({
        studentId: z.string().uuid(),
        firstName: z.string().nullable(),
        lastName: z.string().nullable(),
    })),
    /** One entry per student per objective they have touched or been assigned. */
    progress: z.array(ObjectiveProgressSchema.extend({ studentId: z.string().uuid() })),
});
//# sourceMappingURL=teacher.js.map